import styled from "styled-components";
import { theme } from "../styles";

/** Header tokens — dark by default; light overrides via html[data-theme="light"]. */
const headerVars = `
  --hp-header-bg: hsl(var(--hp-background) / 0.8);
  --hp-header-border: ${theme.borderSoft};
  --hp-header-text: ${theme.text};
  --hp-header-muted: ${theme.textMuted};
  --hp-header-hover: hsl(var(--hp-foreground) / 0.06);
  --hp-header-surface: hsl(var(--hp-card) / 0.4);
  --hp-header-surface-strong: hsl(var(--hp-card) / 0.7);
  --hp-header-panel: hsl(var(--hp-card));
  --hp-header-identity: hsl(var(--hp-muted) / 0.5);
  --hp-header-mobile-bg: hsl(var(--hp-background) / 0.95);
  --hp-header-btn-fg: ${theme.primaryForeground};
  --hp-header-avatar-bg: ${theme.secondary};
  --hp-header-toggle-off: hsl(var(--hp-border));

  html[data-theme="light"] & {
    --hp-header-bg: hsl(0 0% 100% / 0.88);
    --hp-header-border: hsl(var(--hp-border) / 0.8);
    --hp-header-text: ${theme.text};
    --hp-header-muted: ${theme.textMuted};
    --hp-header-hover: hsl(var(--hp-secondary));
    --hp-header-surface: hsl(0 0% 100% / 0.75);
    --hp-header-surface-strong: hsl(230 20% 97%);
    --hp-header-panel: hsl(0 0% 100%);
    --hp-header-identity: hsl(var(--hp-muted));
    --hp-header-mobile-bg: hsl(0 0% 100% / 0.98);
    --hp-header-btn-fg: ${theme.primaryForeground};
    --hp-header-avatar-bg: hsl(230 15% 92%);
    --hp-header-toggle-off: hsl(230 12% 82%);
  }
`;

export const HeaderBar = styled.header`
  ${headerVars}
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1100;
  background: var(--hp-header-bg);
  border-bottom: 1px solid var(--hp-header-border);
  backdrop-filter: blur(20px);
`;

export const HeaderSpacer = styled.div`
  height: 64px;
  flex-shrink: 0;
`;

export const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  max-width: 1400px;
  height: 64px;
  margin: 0 auto;
  padding: 0 12px;
  min-width: 0;

  @media (min-width: 640px) {
    gap: 12px;
    padding: 0 16px;
  }

  @media (min-width: 768px) {
    gap: 16px;
    padding: 0 24px;
  }

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: minmax(11rem, max-content) max-content minmax(0, max-content);
    align-items: center;
    justify-content: space-between;
    column-gap: 16px;
  }
`;

export const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 8px;

  @media (min-width: 400px) {
    gap: 16px;
  }

  @media (min-width: 1024px) {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    gap: 24px;
  }
`;

export const HeaderSearch = styled.form`
  display: none;
  position: relative;
  flex: 1 1 auto;
  width: 10rem;
  min-width: 0;
  max-width: 100%;

  @media (min-width: 768px) {
    display: block;
  }

  @media (min-width: 1280px) {
    width: 14rem;
  }
`;

export const HeaderSearchIcon = styled.span`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  display: flex;
  width: 16px;
  height: 16px;
  color: ${theme.textMuted};
  transform: translateY(-50%);

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const HeaderSearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 12px 0 36px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 6px;
  background: hsl(var(--hp-background) / 0.5);
  color: ${theme.foreground};
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.25;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: ${theme.textMuted};
  }

  &:focus {
    border-color: ${theme.primary};
    background: hsl(var(--hp-background) / 0.7);
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.2);
  }
`;

export const BrandLink = styled.a`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  min-width: 0;
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`;

export const BrandMark = styled.img`
  width: auto;
  height: 32px;
  flex-shrink: 0;
  object-fit: contain;

  @media (min-width: 400px) {
    height: 36px;
  }
`;

export const BrandText = styled.span`
  /* Wordmark hidden below 400px so 375px never overflows */
  display: none;
  color: var(--hp-header-text);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: nowrap;

  @media (min-width: 400px) {
    display: inline;
  }
`;

export const BrandAccent = styled.span`
  background-image: ${theme.gradientGold};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

export const Nav = styled.nav`
  display: none;
  align-items: center;
  justify-content: center;
  gap: 24px;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

export const NavLinkItem = styled.a`
  color: ${({ $active }) =>
    $active ? theme.primary : "var(--hp-header-muted)"};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s ease, border-color 0.2s ease;
  border-bottom: 2px solid
    ${({ $active, $showBorder }) =>
      $showBorder && $active ? theme.primary : "transparent"};
  padding-bottom: ${({ $showBorder }) => ($showBorder ? "2px" : "0")};

  &:hover {
    color: ${({ $active }) =>
      $active ? theme.primary : "var(--hp-header-text)"};
    text-decoration: none;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  gap: 4px;
  min-width: 0;

  @media (min-width: 400px) {
    gap: 8px;
  }

  @media (min-width: 640px) {
    gap: 12px;
  }
`;

export const ThemeToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--hp-header-muted);
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &:hover {
    color: var(--hp-header-text);
    background: var(--hp-header-hover);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }

  @media (min-width: 400px) {
    width: 36px;
    height: 36px;
  }
`;

export const SignInButton = styled.a`
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 36px;
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background: transparent;
  color: hsl(var(--hp-foreground));
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: hsl(var(--hp-secondary));
    color: hsl(var(--hp-foreground));
  }

  @media (min-width: 640px) {
    display: inline-flex;
  }
`;

/** Lovable Navbar: Button variant="hero" size="sm" */
export const SubscribeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 36px;
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background-image: var(--hp-gradient-gold);
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: hsl(var(--hp-primary-foreground));
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 400px) {
    padding: 0 16px;
  }
`;

export const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--hp-header-muted);
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  .material-icons {
    font-size: 20px;
  }

  &:hover {
    color: var(--hp-header-text);
    background: var(--hp-header-hover);
  }

  @media (min-width: 400px) {
    width: 36px;
    height: 36px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileSearchButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  color: var(--hp-header-muted);
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: var(--hp-header-text);
    background: var(--hp-header-hover);
    text-decoration: none;
  }

  @media (min-width: 400px) {
    width: 36px;
    height: 36px;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

export const MobileSheetScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: hsl(0 0% 0% / 0.55);

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileSheetPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1201;
  display: flex;
  flex-direction: column;
  width: min(320px, 85vw);
  border-left: 1px solid var(--hp-header-border);
  background: hsl(var(--hp-card) / 0.95);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: ${theme.shadowCard};

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  padding-right: 48px;
  border-bottom: 1px solid var(--hp-header-border);
`;

export const MobileSheetTitle = styled.p`
  margin: 0;
  color: hsl(var(--hp-foreground));
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.2;
`;

/** Lovable/shadcn SheetContent default close button */
export const MobileSheetClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: transparent;
  color: hsl(var(--hp-foreground));
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: none;
    box-shadow:
      0 0 0 2px hsl(var(--hp-card)),
      0 0 0 4px hsl(var(--hp-ring));
  }
`;

export const MobileSheetNav = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  padding: 12px;
  -webkit-overflow-scrolling: touch;
`;

export const MobileSheetLink = styled.a`
  display: block;
  padding: 10px 12px;
  border-radius: 8px;
  color: ${({ $active }) =>
    $active ? theme.primary : "hsl(var(--hp-foreground))"};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  line-height: 1.35;
  text-decoration: none;
  background: ${({ $active }) =>
    $active ? "hsl(var(--hp-primary) / 0.1)" : "transparent"};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: ${({ $active }) =>
      $active ? theme.primary : "hsl(var(--hp-foreground))"};
    background: ${({ $active }) =>
      $active ? "hsl(var(--hp-primary) / 0.1)" : "hsl(var(--hp-muted) / 0.4)"};
    text-decoration: none;
  }
`;

export const MobileSheetFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid var(--hp-header-border);
`;

export const MobileSheetPrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background-image: var(--hp-gradient-gold);
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: hsl(var(--hp-primary-foreground));
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;

  .material-icons {
    font-size: 18px;
  }

  &:hover {
    text-decoration: none;
    color: hsl(var(--hp-primary-foreground));
  }
`;

export const MobileSheetSecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.7);
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background: transparent;
  color: var(--hp-header-text);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  .material-icons {
    font-size: 18px;
  }

  &:hover {
    background: var(--hp-header-hover);
    text-decoration: none;
    color: var(--hp-header-text);
  }
`;

export const MobileSheetLogoutBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.7);
  border-radius: var(--hp-radius-md, calc(var(--hp-radius, 0.75rem) - 2px));
  background: transparent;
  color: ${theme.primary};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  .material-icons {
    font-size: 18px;
  }

  &:hover {
    background: hsl(var(--hp-primary) / 0.08);
  }
`;

export const MemberBellButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--hp-header-muted);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  .material-icons {
    font-size: 20px;
  }

  &:hover {
    color: var(--hp-header-text);
    background: var(--hp-header-hover);
    text-decoration: none;
  }
`;

export const MemberMenuRoot = styled.div`
  position: relative;
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 240px;
`;

export const MemberMenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 4px 12px 4px 4px;
  border: 1px solid var(--hp-header-border);
  border-radius: 999px;
  background: var(--hp-header-surface);
  color: var(--hp-header-text);
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  .material-icons:last-child {
    color: var(--hp-header-muted);
    font-size: 18px;
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] .material-icons:last-child {
    transform: rotate(180deg);
  }

  &:hover,
  &[aria-expanded="true"] {
    background: var(--hp-header-surface-strong);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }
`;

export const MemberAvatar = styled.img`
  flex-shrink: 0;
  width: ${({ $large }) => ($large ? "48px" : "32px")};
  height: ${({ $large }) => ($large ? "48px" : "32px")};
  border: 1px solid var(--hp-header-border);
  border-radius: 50%;
  object-fit: cover;
`;

export const MemberAvatarFallback = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ $large }) => ($large ? "48px" : "32px")};
  height: ${({ $large }) => ($large ? "48px" : "32px")};
  border: 1px solid var(--hp-header-border);
  border-radius: 50%;
  background: var(--hp-header-avatar-bg);
  color: var(--hp-header-text);
  font-size: ${({ $large }) => ($large ? "0.875rem" : "0.6875rem")};
  font-weight: 700;
  line-height: 1;
`;

export const MemberMenuName = styled.span`
  overflow: hidden;
  min-width: 0;
  color: var(--hp-header-text);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 639px) {
    display: none;
  }
`;

export const MemberMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 1300;
  width: min(288px, calc(100vw - 24px));
  padding: 12px;
  border: 1px solid var(--hp-header-border);
  border-radius: 16px;
  background: var(--hp-header-panel);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: ${theme.shadowCard};
`;

export const MemberMenuIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--hp-header-border);
  border-radius: 12px;
  background: var(--hp-header-identity);
`;

export const MemberMenuIdentityText = styled.div`
  min-width: 0;
`;

export const MemberMenuIdentityName = styled.div`
  overflow: hidden;
  color: var(--hp-header-text);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MemberMenuIdentityEmail = styled.div`
  overflow: hidden;
  margin-top: 2px;
  color: var(--hp-header-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MemberMenuActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
`;

export const MemberMenuAction = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  text-decoration: none !important;
  color: inherit;

  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none !important;
    color: inherit;
  }

  &:hover {
    background: var(--hp-header-hover);
  }
`;

export const MemberMenuActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: hsl(32 95% 55% / 0.15);
  color: ${theme.primary};
  text-decoration: none !important;

  .material-icons {
    font-size: 18px;
    text-decoration: none !important;
  }
`;

export const MemberMenuActionLabel = styled.span`
  color: ${({ $danger }) =>
    $danger ? theme.primary : "var(--hp-header-text)"};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
  text-decoration: none !important;
`;

export const MemberMenuDivider = styled.div`
  height: 1px;
  margin: 12px 0;
  background: var(--hp-header-border);
`;

export const MemberMenuFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 4px;
`;

export const MemberMenuFooterLabel = styled.span`
  color: var(--hp-header-text);
  font-size: 0.875rem;
  font-weight: 600;
`;

export const MemberThemeToggle = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: ${({ $on }) =>
    $on ? theme.primary : "var(--hp-header-toggle-off)"};
  cursor: pointer;
  transition: background 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? "23px" : "3px")};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }
`;
